<script setup lang="ts">
import { ref, computed } from "vue";
import { CalendarOff } from "@lucide/vue";
import { Skeleton } from "@/components/ui/skeleton";
import CardErrorState from "@/components/ui/CardErrorState.vue";
import ForecastPeriod from "./ForecastPeriod.vue";
import type { DayForecast } from "@/models/forecast";
import type { TemperatureUnit } from "@/stores/settings.store";

const props = defineProps<{
  days: DayForecast[];
  wallMode?: boolean;
  tabletMode?: boolean;
  fullscreenMode?: boolean;
  unit: TemperatureUnit;
  selectedIndex?: number;
  loading?: boolean;
  error?: boolean;
}>();

const emit = defineEmits<{
  (e: "daySelected", index: number): void;
}>();

// Local selected state (used when prop not provided)
const localSelectedIndex = ref(0);

// Use prop if provided, otherwise local state
const activeIndex = computed(
  () => props.selectedIndex ?? localSelectedIndex.value,
);

const TABLET_VISIBLE_CARDS = 6;

const gridStyle = computed(() => {
  const count = props.days.length;
  if (props.wallMode) {
    const visibleCount = Math.min(count, TABLET_VISIBLE_CARDS);
    const ratio = visibleCount ? count / visibleCount : 1;
    return {
      gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      minWidth: props.tabletMode
        ? `calc(${ratio * 100}% + ${(ratio - 1) * 6}px)`
        : undefined,
    };
  }
  if (props.tabletMode) {
    // Force cards wide enough so only 6 fit visible, rest scroll
    return {
      gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))`,
      minWidth: `${(count / TABLET_VISIBLE_CARDS) * 100}%`,
    };
  }
  return {
    gridTemplateColumns: `repeat(${count}, minmax(120px, 1fr))`,
    minWidth: `${count * 130}px`,
  };
});

/** Select a forecast day and notify the parent. */
function selectCard(index: number) {
  localSelectedIndex.value = index;
  emit("daySelected", index);
}

</script>

<template>
  <div class="forecast-wrapper m-0 min-h-0" :aria-busy="loading">
    <!-- Loading State -->
    <div
      v-if="loading && !wallMode"
      class="scrollbar-hide -mx-4 overflow-x-auto px-4 py-4"
    >
      <div
        class="grid gap-2"
        :style="{
          gridTemplateColumns: 'repeat(8, minmax(120px, 1fr))',
          minWidth: '1040px',
        }"
      >
        <div
          v-for="i in 8"
          :key="i"
          class="forecast-card relative cursor-pointer rounded-control border border-app-materialEdge bg-app-quiet p-3 shadow-quiet transition-[border-color] duration-150 ease-[ease] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transform-none! motion-reduce:transition-none! [&.forecast-card-hover:hover]:border-accent [&.forecast-card-selected]:z-10 [&:active:not(.forecast-card-selected)]:scale-[0.98]"
          :class="
            wallMode
              ? '[container-type:inline-size] h-full min-w-0 snap-start'
              : 'min-h-[460px] md:max-xl:min-h-[420px]'
          "
        >
          <!-- Day pill skeleton -->
          <div class="flex justify-center pt-1 pb-3">
            <Skeleton class="h-8 w-16 rounded-full" />
          </div>

          <!-- Day section skeleton -->
          <div class="mb-4 flex flex-col items-center gap-2">
            <div class="flex items-center gap-2">
              <Skeleton class="h-12 w-12 rounded-xl" />
              <Skeleton class="h-8 w-14" />
            </div>
            <Skeleton class="h-4 w-20" />
            <Skeleton class="h-4 w-16" />
            <Skeleton class="h-6 w-14 rounded-full" />
          </div>

          <!-- Divider skeleton -->
          <div class="flex justify-center py-2">
            <Skeleton class="h-1 w-12 rounded-full" />
          </div>

          <!-- Night section skeleton -->
          <div class="flex flex-col items-center gap-2 pt-2">
            <Skeleton class="h-3 w-10" />
            <div class="flex items-center gap-2">
              <Skeleton class="h-12 w-12 rounded-xl" />
              <Skeleton class="h-8 w-14" />
            </div>
            <Skeleton class="h-4 w-20" />
            <Skeleton class="h-6 w-14 rounded-full" />
          </div>
        </div>
      </div>
    </div>

    <p
      v-else-if="loading"
      class="grid h-full place-items-center text-text-muted"
      role="status"
    >
      Loading the 8-day forecast…
    </p>

    <!-- Keep unavailable forecasts inside the normal forecast surface. -->
    <div
      v-else-if="!days.length"
      class="min-h-[220px] overflow-hidden rounded-control border border-app-materialEdge bg-app-quiet shadow-quiet"
    >
      <CardErrorState
        class="min-h-[220px]"
        :icon="CalendarOff"
        message="8-day forecast unavailable"
        description="Daily predictions aren’t available for this location right now. Try refreshing later."
      />
    </div>

    <!-- Horizontal scrollable container -->
    <div v-else class="forecast-content relative">
      <!-- Gradient fade edges (tablet only, hidden on desktop where all cards fit) -->
      <div
        v-if="!wallMode"
        class="pointer-events-none absolute top-2 bottom-2 left-0 z-10 w-8 rounded-l-2xl bg-gradient-to-r from-app-bg to-transparent md:top-1 md:bottom-1 xl:top-4 xl:bottom-4 xl:hidden"
      />
      <div
        v-if="!wallMode"
        class="pointer-events-none absolute top-2 right-0 bottom-2 z-10 w-8 rounded-r-2xl bg-gradient-to-l from-app-bg to-transparent md:top-1 md:bottom-1 xl:top-4 xl:bottom-4 xl:hidden"
      />
      <div
        class="forecast-scroll"
        :class="
          wallMode
            ? 'm-0 snap-x snap-proximity [scrollbar-width:thin] overflow-x-auto overflow-y-hidden p-0.5 [&::-webkit-scrollbar]:block [&::-webkit-scrollbar]:h-1.5'
            : 'scrollbar-hide -mx-4 overflow-x-auto overflow-y-hidden px-4 py-4 md:-mx-1 md:px-1 md:py-1 xl:-mx-4 xl:px-4 xl:py-4'
        "
      >
        <div
          class="forecast-grid grid"
          :class="
            wallMode
              ? fullscreenMode
                ? 'auto-rows-[minmax(max(330px,42dvh),auto)] gap-1.5 short-wall:auto-rows-[minmax(max(300px,42dvh),auto)] compact-wall:auto-rows-[minmax(max(280px,44dvh),auto)]'
                : 'auto-rows-[minmax(330px,auto)] gap-1.5 short-wall:auto-rows-[minmax(300px,auto)] compact-wall:auto-rows-[minmax(280px,auto)]'
              : 'gap-2 md:gap-1 xl:gap-2'
          "
          :style="gridStyle"
        >
          <!-- Day Card -->
          <div
            v-for="day in days"
            :key="day.calendarDayIndex"
            class="forecast-card relative cursor-pointer rounded-control border border-app-materialEdge bg-app-quiet shadow-quiet transition-[border-color] duration-150 ease-[ease] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent motion-reduce:transform-none! motion-reduce:transition-none! [&.forecast-card-hover:hover]:border-accent [&.forecast-card-selected]:z-10 [&:active:not(.forecast-card-selected)]:scale-[0.98]"
            :class="[
              wallMode
                ? '[container-type:inline-size] h-full min-w-0 snap-start'
                : 'min-h-[460px] md:max-xl:min-h-[420px]',
              {
                'forecast-card-selected': activeIndex === day.calendarDayIndex,
                'forecast-card-hover': activeIndex !== day.calendarDayIndex,
              },
            ]"
            @click="selectCard(day.calendarDayIndex)"
            role="button"
            tabindex="0"
            :aria-label="`${day.day}: high ${day.high}°, low ${day.low}°`"
            :aria-pressed="activeIndex === day.calendarDayIndex"
            @keydown.enter="selectCard(day.calendarDayIndex)"
            @keydown.space.prevent="selectCard(day.calendarDayIndex)"
          >
            <!-- Content -->
            <div
              class="forecast-periods relative z-10 grid h-full grid-cols-1 grid-rows-[minmax(min-content,1fr)_1px_minmax(min-content,1fr)]"
            >
              <ForecastPeriod
                :label="day.day"
                is-day
                :weather-code="day.dayWeatherCode"
                :temperature="day.high"
                :condition="day.dayCondition"
                :pop="day.dayPop"
                :rain="day.dayRain"
                :ice="day.dayIce"
                :snow="day.daySnow"
                :wind-speed="day.windSpeed"
                :wind-direction="day.windDirection"
                :is-today="day.calendarDayIndex === 0"
                :wall-mode="wallMode"
                :tablet-mode="tabletMode"
                :unit="unit"
              />

              <!-- Divider -->
              <div class="mx-auto h-px w-[calc(100%-32px)] bg-app-border" />

              <ForecastPeriod
                label="Night"
                :is-day="false"
                :weather-code="day.nightWeatherCode"
                :temperature="day.low"
                :condition="day.nightCondition"
                :pop="day.nightPop"
                :rain="day.nightRain"
                :ice="day.nightIce"
                :snow="day.nightSnow"
                :wall-mode="wallMode"
                :tablet-mode="tabletMode"
                :unit="unit"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
